import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { useAnalystNotes } from "../analystNotes/api/useAnalystNotes";
import ActionsPopover from "./ActionsPopover";
import AnalystNotesModal from "./AnalystNotesModal";
import InternalAuditDrawer from "./InternalAuditDrawer";

type OpenPanel = "analyst-notes" | "internal-audit" | "actions" | null;

interface ActionFabClusterProperties {
  /** The deal / approval whose data the Internal / Audit drawer shows. */
  approvalId: string;
}

/**
 * Fixed-position cluster of three floating action buttons anchored to the
 * bottom-right of the viewport. Enforces a single-open-panel invariant:
 * opening any panel automatically closes the others.
 *
 * Built as a self-contained feature — can be hoisted from ReviewPage to a
 * global shell with zero changes to this component.
 *
 * z-index: 40 sits above the page grid and footer (z-10 / z-20) but below
 * Radix portals (z-50) so that the popover/drawer overlays render on top.
 */
function ActionFabCluster({ approvalId }: ActionFabClusterProperties) {
  const { t } = useTranslation();
  const [openPanel, setOpenPanel] = useState<OpenPanel>(null);

  const handleOpenChange = (panel: NonNullable<OpenPanel>) => (shouldOpen: boolean) => {
    setOpenPanel(shouldOpen ? panel : null);
  };

  // Auto-open the Analyst Notes modal on first load when the deal already has
  // notes (caps2 parity). The ref latches on the FIRST settled fetch regardless
  // of count, so a re-render, a panel switch, or a later save that repopulates
  // the list (e.g. adding the first note to a previously-empty deal) never
  // re-triggers the auto-open — it fires at most once per mount.
  const { data: analystNotes } = useAnalystNotes(approvalId);
  const hasAutoOpened = useRef(false);
  useEffect(() => {
    if (hasAutoOpened.current || analystNotes === undefined) {
      return;
    }
    hasAutoOpened.current = true;
    if (analystNotes.length > 0) {
      setOpenPanel("analyst-notes");
    }
  }, [analystNotes]);

  return (
    /* The cluster itself is z-40; Radix portals render at z-50 automatically. */
    <div
      role="region"
      aria-label={t("actions.quickActions")}
      className="fixed bottom-9 right-6 z-40 flex items-center gap-3 md:bottom-11 md:right-8"
    >
      <AnalystNotesModal
        open={openPanel === "analyst-notes"}
        onOpenChange={handleOpenChange("analyst-notes")}
        approvalId={approvalId}
      />
      <InternalAuditDrawer
        open={openPanel === "internal-audit"}
        onOpenChange={handleOpenChange("internal-audit")}
        approvalId={approvalId}
      />
      <ActionsPopover
        open={openPanel === "actions"}
        onOpenChange={handleOpenChange("actions")}
        approvalId={approvalId}
      />
    </div>
  );
}

export default ActionFabCluster;
